import React, { useMemo, useState, useCallback } from "react";
import { Col, Button, Row } from "react-bootstrap";
import { ImagePlus, CheckCircle2 } from "lucide-react";
import { Upload, message } from "antd";
import { useTranslation } from "react-i18next";
import { uploadProductImages } from "../api/imageApi";

const { Dragger } = Upload;

const Photos = ({ productId, onSave }) => {
  const { t } = useTranslation();
  const [saved, setSaved] = useState(false);

  // Local image state
  const [images, setImages] = useState([]);

  // Convert to Upload component fileList
  const fileList = useMemo(
    () =>
      images.map((img) => ({
        uid: img.uid,
        name: img.name,
        status: "done",
        url: img.preview,
        originFileObj: img.file,
      })),
    [images]
  );

  const handleChange = useCallback(
    ({ fileList }) =>
      setImages(
        fileList.map((f) => {
          const existing = images.find((v) => v.uid === f.uid);
          const origin = f.originFileObj;

          const preview =
            existing?.preview ||
            f.url ||
            (origin instanceof File ? URL.createObjectURL(origin) : undefined);

          return {
            uid: f.uid,
            name: f.name,
            file: origin || existing?.file,
            preview
          };
        })
      ),
    [images]
  );

  const handleRemove = useCallback(
    (file) => {
      const found = images.find((v) => v.uid === file.uid);
      if (found?.preview?.startsWith("blob:")) {
        URL.revokeObjectURL(found.preview);
      }
      return true;
    },
    [images]
  );

  const handlePreview = useCallback(
    (file) => {
      const found = images.find((v) => v.uid === file.uid);
      const url = file.url || found?.preview;

      if (url) window.open(url, "_blank");
      else if (file.originFileObj) {
        window.open(URL.createObjectURL(file.originFileObj), "_blank");
      }
    },
    [images]
  );

  const handleSave = async () => {
    try {
      const productId = localStorage.getItem("product_id_save");
      await uploadProductImages(productId, images);
      message.success(t("photos_saved_success"));
      setSaved(true);

      // Notify parent modal
      if (onSave) onSave();
    } catch (err) {
      message.error(t("upload_failed") || "Upload failed");
    }
  };

  if (saved) {
    return (
      <Col md="12" className="text-center">
        <CheckCircle2 size={64} color="#28a745" className="mb-3" />
        <h4 className="fw-bold text-dark mb-2">{t("photos_saved_success")}</h4>
        <p className="text-muted mb-4">{t("you_can_now_continue")}</p>

        <Row className="d-flex justify-content-center gap-2">
          <Col md="5">
            <Button
              variant="outline-secondary"
              className="btn-lg fs-6 py-3 rounded-5 w-100"
              onClick={() => window.location.href = "/"}
            >
              {t("go_home")}
            </Button>
          </Col>
          <Col md="5">
            <Button
              variant="primary"
              className="btn-lg fs-6 py-3 rounded-5 w-100"
              onClick={() => window.location.href = `/products/${productId}`}
            >
              {t("view_product")}
            </Button>
          </Col>
        </Row>
      </Col>
    );
  }

  return (
    <Col md="12">
      <h4 className="mb-3 fw-bold text-dark">{t("add_photos")}</h4>
      <p className="fs-6 fw-light mb-2">{t("photos_text")}</p>

      <Dragger
        accept="image/*"
        multiple
        listType="picture-card"
        beforeUpload={() => false}
        fileList={fileList}
        onChange={handleChange}
        onRemove={handleRemove}
        onPreview={handlePreview}
        style={{ background: "#fff" }}
      >
        <div
          style={{
            height: 120,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div className="text-dark d-flex flex-column align-items-center">
            <ImagePlus size={24} strokeWidth={1.5} />
            <div className="fs-6 fw-bold my-2">{t("drag_photos")}</div>
            <p className="fs-6 text-decoration-underline mb-0">
              {t("download_from_device")}
            </p>
          </div>
        </div>
      </Dragger>

      <Row className="mt-3 d-flex justify-content-center gap-2">
        <Col md="5">
          <Button
            variant="outline-danger"
            className="btn-lg fs-6 py-3 rounded-5 w-100"
            onClick={() => window.location.href = "/dashboard"}
          >
            {t("skip_and_go_home")}
          </Button>
        </Col>
        <Col md="5">
          <button
            type="button"
            className="btn-lg fs-6 py-3 rounded-5 btn-blue w-100"
            onClick={handleSave}
          >
            {t("save")}
          </button>
        </Col>
      </Row>
    </Col>
  );
};

export default Photos;
